import { mixpanelClient } from './mixpanel';

/**
 * Screens that carry their own identity via the `screen` property instead of baking it into the
 * event name, so a component shared across screens can track correctly no matter which one mounts it.
 */
export type DashboardScreen =
  | 'donations_dashboard'
  | 'recipients_dashboard'
  | 'partner_donation_detail'
  | 'recipient_donation_detail'
  | 'pages'
  | 'payouts'
  | 'settings_recipient_admins'
  | 'page_detail'
  | 'admins'
  | 'admin_detail'
  | 'recipients'
  | 'recipient_detail'
  | 'filters'
  | 'filter_detail'
  | 'shopping_cart_detail'
  | 'stripe_onboarding';

export type PageCategory = 'donate' | 'event' | 'recommendation' | 'community';

export type PageInteractionType = 'create' | 'edit' | 'duplicate';

// A duplicate loads the source page as if editing it, so the duplicate flag wins over edit.
export const getPageInteractionType = (
  isEditing: boolean,
  isDuplicating: boolean
): PageInteractionType => {
  if (isDuplicating) return 'duplicate';
  return isEditing ? 'edit' : 'create';
};

export type AdminContextProperties = {
  admin_type: string;
  client_id: number | null;
  recipient_id: string | null;
};

/**
 * Central typed catalog of Mixpanel events: key is the Title Case "<Object> <Action>" event
 * name, value is its snake_case property shape. Events scoped to a screen carry a `screen`
 * property rather than a name prefix. `docs/mixpanel-event-taxonomy.md` documents where each fires.
 */
export type MixpanelEventMap = {
  'User Identified': { user_id: string };
  'Date Filter Changed': { screen: DashboardScreen; filter_label: string };
  'Custom Date Range Selected': { screen: DashboardScreen; start_date: string; end_date: string };
  'Recent Activity Toggled': {
    screen: DashboardScreen;
    view: 'desktop' | 'mobile';
    expanded: boolean;
  };
  'Summary Table Dimension Changed': { screen: DashboardScreen; dimension: string };
  'Summary Table Paginated': {
    screen: DashboardScreen;
    direction: 'next' | 'previous';
    page: number;
  };
  'Carousel Slide Changed': {
    screen: DashboardScreen;
    method: 'swipe' | 'dot_click';
    direction: 'next' | 'previous';
  };
  'Profile Progress Modal Opened': { screen: DashboardScreen };
  'Profile Progress Modal Closed': { screen: DashboardScreen };
  'Fundraising Link Copied': { screen: DashboardScreen };
  'Fundraising Page Link Clicked': { screen: DashboardScreen };
  'Payouts Link Clicked': { screen: DashboardScreen };
  'Help Center Clicked': { screen: DashboardScreen };
  'Stripe Onboarding Button Clicked': {
    screen: DashboardScreen;
    action: 'onboarding' | 'account_update';
  };
  'Payout Report Link Clicked': { screen: DashboardScreen };
  'Donation Detail Tab Changed': { screen: DashboardScreen; tab: string };
  'Table Search Submitted': { screen: DashboardScreen };
  'Table Column Sorted': { screen: DashboardScreen; sort_key?: string | null; reverse?: boolean };
  'Table Columns Saved': { screen: DashboardScreen; visible_columns: string[] };
  'Table Columns Saved As Default': { screen: DashboardScreen; visible_columns: string[] };
  'Table Exported To CSV': { screen: DashboardScreen };
  'Table Paginated': { screen: DashboardScreen; direction: 'next' | 'previous'; page: number };
  'Report Exported':
    | {
        screen: DashboardScreen;
        report_type: 'summary' | 'detailed_recipient';
        start_date: string;
        end_date: string;
        recipient_names: string[];
        recipient_count: number;
      }
    | { screen: DashboardScreen; report_type: 'event'; event_slug: string };
  'Recipient Filter Reset': { screen: DashboardScreen };
  'Pages Filter Changed': {
    screen: DashboardScreen;
    page_category: PageCategory;
    filter: 'all' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  };
  'Create Page Opened': { screen: DashboardScreen; page_category: PageCategory };
  'Create Page Type Selected': {
    screen: DashboardScreen;
    page_category: PageCategory;
    selected_type: PageCategory;
  };
  'Create Page Confirmed': {
    screen: DashboardScreen;
    page_category: PageCategory;
    selected_type: PageCategory;
  };
  'Create Page Canceled': { screen: DashboardScreen; page_category: PageCategory };
  'Page Row Action Clicked': {
    screen: DashboardScreen;
    page_category: PageCategory;
    action: 'edit' | 'delete' | 'preview' | 'duplicate';
    page_id: string;
  };
  'Delete Page Confirmed': {
    screen: DashboardScreen;
    page_category: PageCategory;
    page_id: string;
  };
  'Page Saved': {
    screen: DashboardScreen;
    page_category: PageCategory;
    interaction_type: PageInteractionType;
    page_id: string;
  };
  'Delete Page Canceled': { screen: DashboardScreen; page_category: PageCategory };
  'Payouts Filter Changed': { screen: DashboardScreen; filter: 'all' | 'weekly' | 'quarterly' };
  'Payout Report Row Export Clicked': {
    screen: DashboardScreen;
    report_id: string;
    report_type: 'weekly' | 'quarterly';
  };
  'Recipient Admins Add Opened': { screen: DashboardScreen };
  'Recipient Admins Row Action Clicked': {
    screen: DashboardScreen;
    action: 'view' | 'archive' | 'restore';
  };
  'Recipient Admin Added': { screen: DashboardScreen };
  'Recipient Admin Archived': { screen: DashboardScreen };
  'Recipient Admin Restored': { screen: DashboardScreen };
  'Recipient Badge Section Viewed': {
    screen: DashboardScreen;
    impact_score: number;
    recipient_id: string;
  };
  'Share Score Popup Opened': { screen: DashboardScreen };
  'Share Text Copied': { screen: DashboardScreen };
  'Share Score Platform Selected': {
    screen: DashboardScreen;
    platform: 'x_twitter' | 'facebook' | 'instagram';
  };
  'Resource Library Clicked': { screen: DashboardScreen };
  'Embed Badge Popup Opened': { screen: DashboardScreen };
  'Embed Badge Copied': { screen: DashboardScreen };
  'Admin Logged In': Record<string, never>;
  'Admin Logged Out': Record<string, never>;
  'Sidebar Item Clicked': { item: string };
  'Role Switched': { from_role: string; to_role: string };
  'Client Switched': { client_id: number };
  'Recipient Switched': { recipient_id: string };
  'Terms Accepted': { screen: DashboardScreen };
  'Onboarding Form Submitted': { screen: DashboardScreen };
  'Admin Saved': {
    screen: DashboardScreen;
    interaction_type: 'create' | 'edit';
    role_type: string;
  };
  'Recipient Saved': {
    screen: DashboardScreen;
    interaction_type: 'create' | 'edit';
    recipient_type: string;
  };
  'Recipient Status Toggled': {
    screen: DashboardScreen;
    field: 'is_stripe_authorized' | 'onboarding_form_complete';
    enabled: boolean;
  };
  'Filter Saved': { screen: DashboardScreen; interaction_type: 'create' | 'edit' };
  'Filter Status Changed': { screen: DashboardScreen; status: 'ACTIVE' | 'PAUSED' };
  'Filters Reordered': { screen: DashboardScreen };
  'Shopping Cart Saved': { screen: DashboardScreen; interaction_type: 'activate' | 'edit' };
};

/**
 * Track an event straight off the map. Use this at the call site for events with a single
 * call site. Events with 2+ call sites get a named helper instead, so the properties/tracking
 * line isn't duplicated across files.
 */
export function trackEvent<Name extends keyof MixpanelEventMap>(
  eventName: Name,
  properties: MixpanelEventMap[Name]
): void {
  mixpanelClient.track(eventName, properties);
}

export function trackPageRowActionClicked(
  properties: MixpanelEventMap['Page Row Action Clicked']
): void {
  trackEvent('Page Row Action Clicked', properties);
}

export function trackReportExported(properties: MixpanelEventMap['Report Exported']): void {
  trackEvent('Report Exported', properties);
}

export function trackTablePaginated(properties: MixpanelEventMap['Table Paginated']): void {
  trackEvent('Table Paginated', properties);
}
